import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { I18nextProvider, useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import i18n from "./lang/i18n";
import BaseLayout from "./components/layout/BaseLayout/BaseLayout";
import ProtectedRoute from "./components/routing/ProtectedRoute/ProtectedRoute";
import RequirePermission from "./components/routing/RequirePermission/RequirePermission";
import { UserContext } from "./utils/contexts/UserContext";
import useFindUser from "./utils/hooks/useFindUser";
import {
  DASHBOARD_PERMISSION_CODES,
  MATERIAL_PERMISSION_CODES,
  OBSERVATION_PERMISSION_CODES,
  SCHEDULE_PERMISSION_CODES,
  TEMPLATE_PERMISSION_CODES,
  USER_PERMISSION_CODES,
} from "./utils/permissions";
import Login from "./pages/Auth/Login/Login";
import Invite from "./pages/Auth/Invite/Invite";
import Callback from "./pages/Auth/Callback/Callback";
import Home from "./pages/Home/Home";
import Standards from "./pages/Standards/Standards";
import StandardDetail from "./pages/Standards/StandardDetail";
import Heatmap from "./pages/Heatmap/Heatmap";
import Coverage from "./pages/Coverage/Coverage";
import Observation from "./pages/Observation/Observation";
import MyObservations from "./pages/MyObservations/MyObservations";
import Templates from "./pages/Templates/Templates";
import TemplateEditor from "./pages/Templates/TemplateEditor";
import Users from "./pages/Users/Users";
import ScheduleImport from "./pages/ScheduleImport/ScheduleImport";
import TimetableWorkspace from "./pages/Timetable/TimetableWorkspace";
import LegacyBoardRedirect from "./pages/Timetable/LegacyBoardRedirect";
import TimetableSettings from "./pages/Timetable/SettingsPage";
import TimetableActivities from "./pages/Timetable/ActivitiesPage";
import TimetableClasses from "./pages/Timetable/ClassesPage";
import TimetableRooms from "./pages/Timetable/RoomsPage";
import TimetableConstraints from "./pages/Timetable/ConstraintsPage";
import TimetableGenerate from "./pages/Timetable/GeneratePage";
import TimetableGrid from "./pages/Timetable/GridPage";
import TimetableBoard from "./pages/Timetable/BoardPage";
import Materials from "./pages/Materials/Materials";
import MaterialsLibrary from "./pages/Materials/MaterialsLibrary";
import MaterialDetail from "./pages/Materials/MaterialDetail";
import AdminHub from "./pages/Admin/AdminHub";
import Profile from "./pages/Profile/Profile";
import SupportList from "./pages/Support/SupportList";
import SupportNew from "./pages/Support/SupportNew";
import SupportThread from "./pages/Support/SupportThread";

// Lazy: the marketing page (big markup + its own stylesheet) splits into its
// own chunk, so signed-in users never download it.
const Landing = lazy(() => import("./pages/Landing/Landing"));

function App() {
  const { user, setUser, isLoading } = useFindUser();
  // Subscribes this component to `languageChanged`, so the key below actually
  // updates. Reading the imported `i18n` singleton directly would not re-render.
  const { i18n: activeI18n } = useTranslation();

  return (
    <BrowserRouter>
      <I18nextProvider i18n={i18n}>
        <UserContext.Provider value={{ user, setUser, isLoading }}>
          <ToastContainer position="top-center" />
          {/* Keyed on the active language so switching REMOUNTS the routed tree.
              That is what makes a full page reload unnecessary: server-owned
              content (subject and rubric names, notification text) is translated
              by the API from Accept-Language, so the client must re-request it —
              and every page fetches in a mount effect, so a remount re-fetches
              the lot without re-downloading the JS bundle.

              BrowserRouter and UserContext sit ABOVE this deliberately: the URL
              and the session must survive the switch. */}
          <Routes key={activeI18n.resolvedLanguage}>
            <Route path="/login" element={<Login />} />
            {/* Public by necessity: the recipient has no session yet, and the
                token in the path is what authorizes the request. */}
            <Route path="/invite/:token" element={<Invite />} />
            {/* Where the identity provider redirects back to. Must match
                the redirect_uri registered with that provider exactly. */}
            <Route path="/auth/callback/:provider" element={<Callback />} />

            {/* Public marketing page at the root for logged-out visitors.
                The route only exists while there is no user, so a signed-in
                account falls through to the protected dashboard below. While
                the session is still bootstrapping we render nothing rather
                than flash the landing at a user who is about to be let in. */}
            {!user?.uuid && (
              <Route
                path="/"
                element={
                  isLoading ? null : (
                    <Suspense fallback={null}>
                      <Landing />
                    </Suspense>
                  )
                }
              />
            )}

            <Route
              element={
                <ProtectedRoute
                  isAllowed={!!user?.uuid}
                  isLoading={isLoading}
                  redirectPath="/login"
                />
              }
            >
              <Route element={<BaseLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/profile" element={<Profile />} />
                {/* Every signed-in user, every role: the API scopes tickets to
                    their author, so there is nothing to gate on. */}
                <Route path="/support" element={<SupportList />} />
                <Route path="/support/new" element={<SupportNew />} />
                <Route path="/support/:uuid" element={<SupportThread />} />
                {/* No RequirePermission: the endpoint behind it is
                    self-scoped — `mine` only ever returns visits you were
                    part of — and the people who conduct observations
                    (evaluator) hold no dashboard permission to gate on. */}
                <Route path="/my-observations" element={<MyObservations />} />
                {/* Multi-permission hub — guards itself (redirects when the
                    user can reach none of its tools). */}
                <Route path="/admin" element={<AdminHub />} />

                <Route
                  element={
                    <RequirePermission
                      permission={DASHBOARD_PERMISSION_CODES.SCHOOL_VIEW}
                    />
                  }
                >
                  <Route path="/standards" element={<Standards />} />
                  <Route path="/standards/:id" element={<StandardDetail />} />
                  <Route path="/heatmap" element={<Heatmap />} />
                  <Route path="/coverage" element={<Coverage />} />
                </Route>

                <Route
                  element={
                    <RequirePermission
                      permission={OBSERVATION_PERMISSION_CODES.CREATE}
                    />
                  }
                >
                  <Route path="/observation" element={<Observation />} />
                </Route>

                <Route
                  element={
                    <RequirePermission
                      permission={TEMPLATE_PERMISSION_CODES.MANAGE}
                    />
                  }
                >
                  <Route path="/templates" element={<Templates />} />
                  <Route path="/templates/:uuid" element={<TemplateEditor />} />
                </Route>

                <Route
                  element={
                    <RequirePermission permission={USER_PERMISSION_CODES.MANAGE} />
                  }
                >
                  <Route path="/users" element={<Users />} />
                </Route>

                <Route
                  element={
                    <RequirePermission permission={SCHEDULE_PERMISSION_CODES.MANAGE} />
                  }
                >
                  {/* The draft board is a full-shell TAKEOVER, not a tab: it
                      sits outside the workspace layout so the tab strip cannot
                      be clicked away mid-edit. */}
                  <Route path="/timetable/draft/:uuid" element={<TimetableBoard />} />

                  {/* One destination, six tabs. The permission is enforced by
                      the RequirePermission wrapper above — the panels used to
                      repeat it inline six times. */}
                  <Route path="/timetable" element={<TimetableWorkspace />}>
                    {/* The LIVE timetable is the landing tab, not Generate.
                        "Timetable" meaning "the form that builds one" is why a
                        manager looking for the school's actual week could not
                        find it — the thing they came for was never on screen. */}
                    <Route index element={<TimetableGrid />} />
                    <Route path="generate" element={<TimetableGenerate />} />
                    <Route path="lessons" element={<TimetableActivities />} />
                    <Route path="classes" element={<TimetableClasses />} />
                    <Route path="rooms" element={<TimetableRooms />} />
                    <Route path="rules" element={<TimetableConstraints />} />
                    <Route path="week" element={<TimetableSettings />} />
                    <Route path="import" element={<ScheduleImport />} />
                  </Route>

                  {/* Pre-consolidation URLs — cheap to keep, and they are in
                      bookmarks and emailed links. Siblings of the layout route,
                      not children: nested, each one mounted the whole workspace
                      shell (back link, tab strip, Outlet) purely to render a
                      redirect and unmount it again. Ranking is unaffected —
                      v6 flattens and scores the same either way, so the static
                      tabs above still beat `:uuid`. */}
                  <Route path="/timetable/settings" element={<Navigate to="/timetable/week" replace />} />
                  <Route path="/timetable/activities" element={<Navigate to="/timetable/lessons" replace />} />
                  <Route path="/timetable/constraints" element={<Navigate to="/timetable/rules" replace />} />
                  <Route path="/schedule-import" element={<Navigate to="/timetable/import" replace />} />
                  <Route path="/timetable/:uuid" element={<LegacyBoardRedirect />} />
                </Route>

                <Route
                  element={
                    <RequirePermission
                      permission={MATERIAL_PERMISSION_CODES.MANAGE}
                    />
                  }
                >
                  <Route path="/materials" element={<Materials />} />
                </Route>

                <Route
                  element={
                    <RequirePermission
                      permission={DASHBOARD_PERMISSION_CODES.TEACHER_VIEW}
                    />
                  }
                >
                  <Route path="/my-materials" element={<MaterialsLibrary />} />
                  <Route
                    path="/my-materials/:uuid"
                    element={<MaterialDetail />}
                  />
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </UserContext.Provider>
      </I18nextProvider>
    </BrowserRouter>
  );
}

export default App;
